import IconButton from '@/components/Buttons/IconButton';
import Dialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useGetBackgroundsList } from '@/hooks/useGetBackgroundsList';
import { useUploadBackgroundImage } from '@/hooks/useUploadBackgroundImage';
import Plus from '@/images/plus.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { ColorControl } from './ColorControl';
import { DefaultBackgrounds } from './DefaultBackgrounds';
import { MyBackgrounds } from './MyBackgrounds';

const StyledDialogTitle = styled(DialogTitle)`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    &.MuiDialogTitle-root {
        padding: 0;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    display: flex;
    flex-direction: row;
    &.MuiDialogActions-root {
        justify-content: flex-end;
        padding: 0;
    }
`;

const StyledTitle = styled.div`
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    letter-spacing: -0.02em;
`;

const StyledSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-bottom: 12px;
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 0;
        gap: 12px;
        ::-webkit-scrollbar {
            display: none;
        }
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
`;

const StyledIconButton = styled(IconButton)`
    color: ${theme.colors.secondary[900]};
`;

const UploadButton = styled(Button)`
    width: 128px;
    margin-left: auto;
    min-height: 44px;
    gap: 8px;
`;

interface Props {
    title: string;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: (background: string) => Promise<void>;
}

export const BackgroundsModal = observer(({ title, isOpen, onClose, onUpdate }: Props) => {
    const mixpanel = useAnalytics();
    const onInputChange = useUploadBackgroundImage();
    const { data: userBackgrounds } = useGetBackgroundsList();

    const [isUploading, setIsUploading] = useState(false);
    const [background, setBackground] = useState<string>('');

    const handleUpdate = useCallback(async () => {
        await onUpdate(background);
    }, [background, onUpdate]);

    const onUploadClick = useCallback(() => {
        mixpanel?.track('agent-update-background', {
            event: 'click',
            agentType: 'upload',
            presenterType: 'talk',
        });
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.oninput = (ev: Event) => {
            setIsUploading(true);
            onInputChange(ev as unknown as React.MouseEvent<HTMLInputElement>, () => setIsUploading(false));
        };

        fileInput.click();
        fileInput.remove();
    }, []);

    return (
        <Dialog
            onClose={onClose}
            open={isOpen}
            width="120rem"
            height="80rem"
            contentstyle={{ padding: '0' }}
            PaperProps={{ style: { borderRadius: '16px', gap: '24px', padding: '24px' } }}>
            <StyledDialogTitle id="backgrounds-gallery">
                <StyledTitle>{title}</StyledTitle>
                <StyledIconButton ariaLabel="close" onClick={onClose}>
                    <CloseIcon fontSize="large" />
                </StyledIconButton>
            </StyledDialogTitle>

            <StyledDialogContent>
                <UploadButton onClick={onUploadClick} size="large" loading={isUploading}>
                    <MaskedIcon src={Plus} size="16px" />
                    Upload
                </UploadButton>

                <StyledSection>
                    <Typography variant="h3">Background color</Typography>
                    <ColorControl value={background} onChange={setBackground} />
                </StyledSection>

                {userBackgrounds !== undefined && userBackgrounds?.length > 0 && (
                    <StyledSection>
                        <Typography variant="h3">My backgrounds</Typography>
                        <MyBackgrounds value={background} onChange={setBackground} />
                    </StyledSection>
                )}

                <StyledSection>
                    <Typography variant="h3">Backgrounds gallery</Typography>
                    <DefaultBackgrounds value={background} onChange={setBackground} />
                </StyledSection>
            </StyledDialogContent>

            <StyledDialogActions>
                <Button color="secondary" onClick={handleUpdate}>
                    Update background
                </Button>
            </StyledDialogActions>
        </Dialog>
    );
});
